<div class="boq-form-container p-6">
  <mat-card>
    <mat-card-header>
      <div class="boq-header">
        <div>
          <mat-card-title>
            {{ mode === 'create' ? 'Create BoQ' : 'Edit BoQ' }}
          </mat-card-title>
          <mat-card-subtitle>
            {{ items.length }} item<span *ngIf="items.length !== 1">s</span>
            · {{ sections.length }} section<span *ngIf="sections.length !== 1">s</span>
            · {{ formatInr(boqGrandTotal) }}
          </mat-card-subtitle>
        </div>
        <div class="header-actions">
          <button type="button" class="ghost-btn" *ngIf="boqId" (click)="openDeviation()">
            <mat-icon>assessment</mat-icon>
            Deviation
          </button>
          <button type="button" class="ghost-btn" (click)="onCancel()">
            <mat-icon>close</mat-icon>
            Close
          </button>
        </div>
      </div>
    </mat-card-header>

    <mat-card-content>
      <form [formGroup]="boqForm" (ngSubmit)="onSubmit()">
        <div class="form-section">
          <h3 class="section-title">Basic information</h3>
          <div class="basic-grid">
            <label class="field">
              <span class="field-label">Project <span class="req">*</span></span>
              <select class="form-control form-control--select" formControlName="project_id" [disabled]="loading">
                <option value="">Select a project</option>
                <option *ngFor="let project of projects" [ngValue]="project.id">
                  {{ project.project_name }}
                </option>
              </select>
              <span class="field-error" *ngIf="boqForm.get('project_id')?.hasError('required') && boqForm.get('project_id')?.touched">
                Project is required
              </span>
            </label>

            <label class="field">
              <span class="field-label">BoQ code <span class="req">*</span></span>
              <input class="form-control" formControlName="boq_code" placeholder="e.g. BOQ-001">
              <span class="field-error" *ngIf="boqForm.get('boq_code')?.hasError('required') && boqForm.get('boq_code')?.touched">
                BoQ code is required
              </span>
            </label>

            <label class="field field--full">
              <span class="field-label">BoQ name <span class="req">*</span></span>
              <input class="form-control" formControlName="boq_name" placeholder="Enter BoQ name">
              <span class="field-error" *ngIf="boqForm.get('boq_name')?.hasError('required') && boqForm.get('boq_name')?.touched">
                BoQ name is required
              </span>
            </label>

            <label class="field field--full">
              <span class="field-label">Description</span>
              <textarea class="form-control form-control--area" formControlName="description" rows="2" placeholder="Optional notes"></textarea>
            </label>
          </div>
        </div>

        <div class="form-section lrs-section" *ngIf="isRoadDomain">
          <div class="section-head">
            <button type="button" class="link-btn" (click)="lrsExpanded = !lrsExpanded">
              <mat-icon>{{ lrsExpanded ? 'expand_less' : 'expand_more' }}</mat-icon>
              LRS corridor (optional)
            </button>
            <button type="button" class="ghost-btn" (click)="openBoqOnMap()">Open on map</button>
          </div>
          <div *ngIf="lrsExpanded" class="lrs-grid">
            <label class="field">
              <span class="field-label">Route</span>
              <select class="form-control form-control--select" formControlName="lrs_route_id">
                <option [ngValue]="null">None</option>
                <option *ngFor="let route of lrsRoutes" [ngValue]="route.id">
                  {{ route.route_code }} — {{ route.route_name }}
                </option>
              </select>
            </label>
            <label class="field">
              <span class="field-label">Chainage label</span>
              <input class="form-control" formControlName="lrs_chainage" placeholder="e.g. NH-44 @ 45+200" />
            </label>
            <label class="field">
              <span class="field-label">From km</span>
              <input class="form-control" type="number" step="0.001" formControlName="lrs_from_km" />
            </label>
            <label class="field">
              <span class="field-label">To km</span>
              <input class="form-control" type="number" step="0.001" formControlName="lrs_to_km" />
            </label>
          </div>
        </div>

        <div class="form-section">
          <h3 class="section-title">Cost percentages</h3>
          <div class="pct-grid">
            <label class="field">
              <span class="field-label">Overhead</span>
              <div class="suffix-field">
                <input class="form-control" type="number" formControlName="overhead_percentage" min="0" max="100" step="0.01">
                <span>%</span>
              </div>
            </label>
            <label class="field">
              <span class="field-label">Profit</span>
              <div class="suffix-field">
                <input class="form-control" type="number" formControlName="profit_percentage" min="0" max="100" step="0.01">
                <span>%</span>
              </div>
            </label>
            <label class="field">
              <span class="field-label">Tax</span>
              <div class="suffix-field">
                <input class="form-control" type="number" formControlName="tax_percentage" min="0" max="100" step="0.01">
                <span>%</span>
              </div>
            </label>
            <label class="field">
              <span class="field-label">Contingency</span>
              <div class="suffix-field">
                <input class="form-control" type="number" formControlName="contingency_percentage" min="0" max="100" step="0.01">
                <span>%</span>
              </div>
            </label>
          </div>
        </div>

        <!-- Assignment & Review (edit mode: planner assigns PM; PM raises requests and approves) -->
        <div *ngIf="mode === 'edit' && boqId" class="form-section assign-section">
          <h3 class="section-title">Assignment &amp; review</h3>
          <div class="assign-meta">
            <div>
              <span class="meta-label">Project manager</span>
              <span class="meta-value">{{ boqDetail?.assigned_to_name || 'Not assigned' }}</span>
            </div>
            <div>
              <span class="meta-label">Status</span>
              <span class="status-pill"
                [ngClass]="{
                  'status-draft': boqDetail?.estimation_status === 'draft' || !boqDetail?.estimation_status,
                  'status-pending': boqDetail?.estimation_status === 'pending_review' || boqDetail?.estimation_status === 'changes_requested',
                  'status-approved': boqDetail?.estimation_status === 'approved',
                  'status-rejected': boqDetail?.estimation_status === 'rejected'
                }">
                {{ (boqDetail?.estimation_status || 'draft') | titlecase }}
              </span>
            </div>
          </div>
          <p *ngIf="boqDetail?.estimation_status === 'approved'" class="ready-note">Ready to publish — send for bidding from the BOQ list.</p>
          <div class="assign-actions">
            <select class="form-control form-control--select assign-select" [(ngModel)]="assignStaffId" [ngModelOptions]="{standalone: true}">
              <option [ngValue]="null">Select project manager</option>
              <option *ngFor="let s of staffList" [ngValue]="s.id">{{ s.full_name }} ({{ s.employee_code || s.designation || '—' }})</option>
            </select>
            <button type="button" class="ghost-btn" (click)="assignToManager()"
              [disabled]="assignStaffId == null || assignSending">
              {{ assignSending ? 'Assigning...' : 'Assign to PM' }}
            </button>
            <button *ngIf="canSubmitForApproval" type="button" class="primary-btn" (click)="submitForApproval()"
              [disabled]="submitSending">
              {{ submitSending ? 'Submitting...' : 'Submit for approval' }}
            </button>
            <button *ngIf="canShowApprovalSection" type="button" class="primary-btn" (click)="approveEstimation()"
              [disabled]="approveSending">
              {{ approveSending ? 'Approving...' : 'Approve estimation' }}
            </button>
            <button type="button" class="ghost-btn" (click)="openReviewRequestForm()" *ngIf="!showReviewRequestForm && canShowApprovalSection">
              Request revision
            </button>
            <button *ngIf="canShowApprovalSection" type="button" class="ghost-btn ghost-btn--warn" (click)="openRejectForm()"
              [disabled]="rejectSending">
              Reject
            </button>
          </div>
          <div *ngIf="showRejectForm" class="border rounded p-3 bg-white mb-4">
            <p class="font-medium mb-2">Reject estimation</p>
            <mat-form-field appearance="outline" class="w-full">
              <mat-label>Reason</mat-label>
              <textarea matInput [(ngModel)]="rejectReason" [ngModelOptions]="{standalone: true}" rows="2" placeholder="Why is this enquiry being rejected?"></textarea>
            </mat-form-field>
            <div class="flex gap-2">
              <button type="button" mat-raised-button color="warn" (click)="rejectEstimation()" [disabled]="rejectSending || !rejectReason.trim()">
                {{ rejectSending ? 'Rejecting...' : 'Confirm reject' }}
              </button>
              <button type="button" mat-stroked-button (click)="cancelRejectForm()">Cancel</button>
            </div>
          </div>
          <div *ngIf="showReviewRequestForm" class="border rounded p-3 bg-white mb-4">
            <p class="font-medium mb-2">New review request</p>
            <mat-form-field appearance="outline" class="w-full max-w-md mr-2">
              <mat-label>Request type</mat-label>
              <mat-select [(ngModel)]="reviewRequestType" [ngModelOptions]="{standalone: true}">
                <mat-option value="update_field">Update field value</mat-option>
                <mat-option value="add_line_item">Add new line item</mat-option>
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline" class="w-full">
              <mat-label>Description</mat-label>
              <textarea matInput [(ngModel)]="reviewRequestDescription" [ngModelOptions]="{standalone: true}" rows="2" placeholder="Describe the change needed"></textarea>
            </mat-form-field>
            <div class="flex gap-2">
              <button type="button" mat-raised-button color="primary" (click)="submitReviewRequest()" [disabled]="reviewRequestSending">
                {{ reviewRequestSending ? 'Submitting...' : 'Submit' }}
              </button>
              <button type="button" mat-stroked-button (click)="cancelReviewRequestForm()">Cancel</button>
            </div>
          </div>
          <div *ngIf="reviewRequests.length > 0">
            <p class="font-medium mb-2">Review requests</p>
            <ul class="list-disc list-inside text-sm space-y-1">
              <li *ngFor="let r of reviewRequests">
                <span class="font-medium">{{ r.request_type | titlecase }}</span> — {{ r.description || '—' }}
                <span class="text-gray-500">({{ r.status }}, by {{ r.requested_by_name || '—' }})</span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Sections -->
        <div class="form-section mb-6">
          <div class="flex justify-between items-center mb-4">
            <h3 class="section-title">Sections</h3>
            <button type="button" mat-raised-button color="primary" (click)="addSection()">
              <mat-icon>add</mat-icon>
              Add Section
            </button>
          </div>

          <div formArrayName="sections" class="overflow-x-auto">
            <table class="boq-table w-full text-sm">
              <thead>
                <tr class="border-b border-gray-200 bg-gray-50">
                  <th class="text-left py-2 px-3 w-12">#</th>
                  <th class="text-left py-2 px-3">Code</th>
                  <th class="text-left py-2 px-3">Section Name</th>
                  <th class="text-left py-2 px-3">Description</th>
                  <th class="text-right py-2 px-3 w-20">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let section of sections.controls; let i = index" [formGroupName]="i" 
                    class="section-row border-b border-gray-100 hover:bg-gray-50">
                  <td class="py-1 px-3 align-middle">{{ i + 1 }}</td>
                  <td class="py-1 px-3">
                    <input class="form-control form-control--table" formControlName="section_code" placeholder="SEC-001">
                  </td>
                  <td class="py-1 px-3">
                    <input class="form-control form-control--table" formControlName="section_name" placeholder="Section name">
                  </td>
                  <td class="py-1 px-3">
                    <input class="form-control form-control--table" formControlName="description" placeholder="Description">
                  </td>
                  <td class="py-1 px-3 text-right">
                    <button type="button" mat-icon-button color="warn" (click)="removeSection(i)">
                      <mat-icon>delete</mat-icon>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Items -->
        <div class="form-section mb-6">
          <div class="flex justify-between items-center mb-4">
            <h3 class="section-title">BoQ Items</h3>
            <button type="button" mat-raised-button color="primary" (click)="addItem()">
              <mat-icon>add</mat-icon>
              Add Item
            </button>
          </div>

          <div formArrayName="items" class="overflow-x-auto">
            <table class="boq-table w-full text-sm">
              <thead>
                <tr class="border-b border-gray-200 bg-gray-50">
                  <th class="text-left py-2 px-2 w-12">S.No</th>
                  <th class="text-left py-2 px-2 min-w-[200px]">Description</th>
                  <th class="text-left py-2 px-2 w-24">Unit</th>
                  <th class="text-right py-2 px-2 w-20">Qty</th>
                  <th class="text-right py-2 px-2 w-24">Rate</th>
                  <th class="text-right py-2 px-2 w-28">Amount</th>
                  <th class="text-center py-2 px-2 w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                <ng-container *ngFor="let item of items.controls; let i = index" [formGroupName]="i">
                  <tr class="item-row border-b border-gray-100 hover:bg-gray-50/50">
                    <td class="py-1 px-2 align-middle">{{ i + 1 }}</td>
                    <td class="py-1 px-2 min-w-[220px]">
                      <input
                        class="form-control form-control--table"
                        formControlName="item_code"
                        placeholder="Search material by code or name..."
                        [matAutocomplete]="materialAuto"
                        (focus)="currentEditingItemIndex = i"
                        (input)="onMaterialInput($event, i)"
                      />
                      <mat-autocomplete #materialAuto="matAutocomplete"
                        (optionSelected)="onMaterialSelected($event, currentEditingItemIndex)"
                        [displayWith]="displayMaterialFn">
                        <mat-option *ngFor="let material of filterMaterials(item.get('item_code')?.value || '')" [value]="material">
                          {{ material.system_code || material.code }} — {{ material.name }}
                        </mat-option>
                      </mat-autocomplete>
                      <div class="item-desc" *ngIf="item.get('item_description')?.value">
                        {{ item.get('item_description')?.value }}
                      </div>
                    </td>
                    <td class="py-1 px-2">
                      <select class="form-control form-control--select form-control--table" formControlName="unit">
                        <ng-container *ngFor="let type of Object.keys(unitsByType)">
                          <optgroup [label]="type | titlecase">
                            <option *ngFor="let unit of unitsByType[type]" [value]="unit.unit_code">
                              {{ unit.unit_code }}
                            </option>
                          </optgroup>
                        </ng-container>
                      </select>
                    </td>
                    <td class="py-1 px-2 text-right">
                      <input class="form-control form-control--table form-control--qty" type="number" formControlName="quantity" min="0" step="0.001">
                    </td>
                    <td class="amount-cell">{{ formatInr(getEffectiveUnitRate(i)) }}</td>
                    <td class="amount-cell amount-cell--total">{{ formatInr(calculateItemTotal(i)) }}</td>
                    <td class="py-1 px-2 text-center">
                      <button type="button" mat-icon-button (click)="toggleItemExpand(i)">
                        <mat-icon>{{ isItemExpanded(i) ? 'expand_less' : 'expand_more' }}</mat-icon>
                      </button>
                      <button type="button" mat-icon-button color="warn" (click)="removeItem(i)">
                        <mat-icon>delete</mat-icon>
                      </button>
                    </td>
                  </tr>
                  <!-- Expandable details row -->
                  <tr *ngIf="isItemExpanded(i)" [formGroupName]="i" class="expanded-details bg-gray-50/80">
                    <td colspan="7" class="py-3 px-4">
                      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                        <mat-form-field class="w-full md:col-span-2" subscriptSizing="dynamic">
                          <mat-label>Description</mat-label>
                          <input matInput formControlName="item_description" placeholder="Item description">
                        </mat-form-field>
                        <mat-form-field class="w-full" subscriptSizing="dynamic">
                          <mat-label>Specification</mat-label>
                          <input matInput formControlName="specification" placeholder="Specifications">
                        </mat-form-field>
                        <mat-form-field class="w-full" subscriptSizing="dynamic">
                          <mat-label>Material ₹</mat-label>
                          <input matInput type="number" formControlName="material_unit_rate" min="0" step="0.01">
                        </mat-form-field>
                        <mat-form-field class="w-full" subscriptSizing="dynamic">
                          <mat-label>Labor ₹</mat-label>
                          <input matInput type="number" formControlName="labor_unit_rate" min="0" step="0.01">
                        </mat-form-field>
                        <mat-form-field class="w-full" subscriptSizing="dynamic">
                          <mat-label>Equipment ₹</mat-label>
                          <input matInput type="number" formControlName="equipment_unit_rate" min="0" step="0.01">
                        </mat-form-field>
                        <mat-form-field class="w-full" subscriptSizing="dynamic">
                          <mat-label>Other ₹</mat-label>
                          <input matInput type="number" formControlName="other_costs" min="0" step="0.01">
                        </mat-form-field>
                        <mat-form-field class="w-full" subscriptSizing="dynamic">
                          <mat-label>Wastage %</mat-label>
                          <input matInput type="number" formControlName="wastage_percentage" min="0" max="100" step="0.01">
                        </mat-form-field>
                        <mat-form-field class="w-full" subscriptSizing="dynamic">
                          <mat-label>Discount %</mat-label>
                          <input matInput type="number" formControlName="discount_percentage" min="0" max="100" step="0.01">
                        </mat-form-field>
                        <mat-form-field class="w-full" subscriptSizing="dynamic">
                          <mat-label>Tax %</mat-label>
                          <input matInput type="number" formControlName="tax_percentage" min="0" max="100" step="0.01">
                        </mat-form-field>
                        <mat-form-field class="w-full md:col-span-2" subscriptSizing="dynamic">
                          <mat-label>Remarks</mat-label>
                          <input matInput formControlName="remarks" placeholder="Remarks">
                        </mat-form-field>
                      </div>
                      <div class="mt-3 pt-3 border-t border-gray-200">
                        <div class="flex justify-between items-center mb-2">
                          <span class="text-xs font-semibold text-gray-600">Resource Inputs</span>
                          <button type="button" mat-stroked-button color="primary" (click)="addResourceInput(i)">
                            <mat-icon class="icon-sm">add</mat-icon> Add
                          </button>
                        </div>
                        <div formArrayName="resource_inputs">
                          <div *ngFor="let res of getResourceInputs(i).controls; let ri = index" [formGroupName]="ri"
                               class="flex flex-wrap items-center gap-2 mb-2">
                            <mat-form-field class="min-w-[100px]" subscriptSizing="dynamic">
                              <mat-select formControlName="breakdown_type">
                                <mat-option value="man">Man</mat-option>
                                <mat-option value="machine">Machine</mat-option>
                                <mat-option value="material">Material</mat-option>
                              </mat-select>
                            </mat-form-field>
                            <mat-form-field class="min-w-[120px]" subscriptSizing="dynamic">
                              <input matInput formControlName="description" placeholder="Desc">
                            </mat-form-field>
                            <mat-form-field class="w-16" subscriptSizing="dynamic">
                              <input matInput type="number" formControlName="quantity" min="0" placeholder="Qty">
                            </mat-form-field>
                            <mat-form-field class="w-16" subscriptSizing="dynamic">
                              <input matInput formControlName="unit" placeholder="Unit">
                            </mat-form-field>
                            <mat-form-field class="w-20" subscriptSizing="dynamic">
                              <input matInput type="number" formControlName="unit_rate" min="0" placeholder="Rate">
                            </mat-form-field>
                            <button type="button" mat-icon-button color="warn" (click)="removeResourceInput(i, ri)">
                              <mat-icon>close</mat-icon>
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </ng-container>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Form Actions -->
        <div class="form-actions sticky-actions">
          <div class="totals">
            <div class="totals__row">
              <span>Subtotal</span>
              <span>{{ formatInr(itemsSubtotal) }}</span>
            </div>
            <div class="totals__row totals__row--total">
              <span>Total</span>
              <span>{{ formatInr(boqGrandTotal) }}</span>
            </div>
          </div>
          <div class="action-btns">
            <button type="button" class="ghost-btn" (click)="onCancel()">Cancel</button>
            <button type="submit" class="primary-btn" [disabled]="submitting || boqForm.invalid">
              {{ submitting ? 'Saving...' : (mode === 'create' ? 'Create BoQ' : 'Update BoQ') }}
            </button>
          </div>
        </div>
      </form>
    </mat-card-content>
  </mat-card>
</div>
